import { GoalPriorityChip } from '../../molecules/GoalPriorityChip/GoalPriorityChip';
import { Box, useTheme, Typography, styled } from '@mui/material';
import dayjs from 'dayjs';
import { sumBy } from 'lodash';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { InfoTooltip } from '../../molecules/InfoTooltip';
import { formatCurrency, getClientJourneyFlowType } from '../../../utils';
import { Column, LabelLarge, SectionBox, StyledChipContainer, ValueText } from '../../../themes/styles/plan-overview';
import { GoalTypeTitlemapping } from '../../../common/types';
import { RETIREMENT_GOAL } from '../../../common/constants';

interface GoalDetailsSectionProps {
    selectedGoalIndex: number;
}

export const ContributionDt = styled(Typography)(({ theme }) => ({
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.semantic.text.muted,
    margin: '0px 0px 5px 0px'
}));

export const ContributionDtValueText = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '28px',
    marginTop: '8px',
    marginBottom: '0px'
}));

export const GoalDetailsSection = ({ selectedGoalIndex }: GoalDetailsSectionProps) => {
    const { t } = useTranslation();
    const { formFields, request, response } = useAppSelector((state) => state.adviceGoals);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const selectedGoal = formFields[selectedGoalIndex]?.data;
    const selectedGoalRequest = request[selectedGoalIndex];
    const selectedGoalResponse = response[selectedGoalIndex];
    const goalId = selectedGoalRequest?.goalId;
    const goePayload = selectedGoalResponse?.goePayload || {};
    const isRecommendationApplied = selectedGoalRequest?.isRecommendationApplied || false;
    const isRetirement =
        selectedGoal?.goalType === GoalTypeTitlemapping.Retirement || selectedGoal?.goalType === RETIREMENT_GOAL;
    const theme = useTheme();
    const adviceGoals = selectedGoalResponse || {};
    const infusionIndex = useRef(0);
    // logic from PlanInfoAndReRunSection.tsx
    const calculateAnnualContribution = (goePayload: any) => {
        let anualContributionDict: any;
        let infusionsArray: { infusion: any; realisationDt: dayjs.Dayjs }[] = [];
        let cashflowDt = dayjs(goePayload?.cashflowDate, 'DD-MM-YYYY');
        let startDt = dayjs(goePayload?.currDate, 'DD-MM-YYYY');
        let endDt = dayjs(goePayload?.endDate, 'DD-MM-YYYY');
        let yrdiff = endDt.diff(startDt, 'year') + 1;
        goePayload?.infusions &&
            goePayload?.infusions.forEach((infusion: any, index: number) => {
                if (index == goePayload?.infusions?.length - 1) {
                    infusionsArray.push({
                        infusion: infusion,
                        realisationDt: endDt
                    });
                    return;
                }
                if (yrdiff < goePayload?.infusions.length && cashflowDt != startDt) {
                    if (index == 0) {
                        infusionsArray.push({
                            infusion: infusion,
                            realisationDt: startDt
                        });
                        return;
                    }
                    index = index - 1;
                }
                infusionsArray.push({
                    infusion: infusion,
                    realisationDt: cashflowDt.add(index, 'month')
                });
            });
        anualContributionDict = {
            infusion: 0,
            realisationDt: startDt.format('DD-MM-YYYY'),
            isFinalYear: infusionsArray.length <= 2
        };
        if (isRecommendationApplied && goalId in adviceGoals.infusionindex && adviceGoals.infusionindex[goalId] !== 0) {
            let lastinfusionindex = adviceGoals.infusionindex[goalId] ?? 0;
            anualContributionDict.infusion = infusionsArray[lastinfusionindex].infusion;
            anualContributionDict.realisationDt = infusionsArray[lastinfusionindex].realisationDt.format('DD-MM-YYYY');
        } else {
            let i = 0;
            for (const element of infusionsArray as any) {
                if (element.infusion > 0) {
                    anualContributionDict.infusion = element.infusion;
                    anualContributionDict.realisationDt = element.realisationDt.format('DD-MM-YYYY');
                    anualContributionDict.isFinalYear = false;
                    infusionIndex.current = i;
                    break;
                }
                i++;
            }
        }
        return anualContributionDict;
    };
    const calculateAmountAfterMonths = (startDateInput: string, endDateInput: string): number => {
        // Helper function to parse DD-MM-YYYY formatted date
        const parseDate = (dateInput: string): Date => {
            const [month, day, year] = dateInput.split('-').map(Number);
            return new Date(year, month - 1, day); // months are 0-indexed in JavaScript Date
        };

        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // current month is 0-indexed, so add 1

        const startDate = parseDate(startDateInput);
        const endDate = parseDate(endDateInput);

        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth() + 1;
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth() + 1;

        //out of range: future or past years
        if (currentYear < startYear || currentYear > endYear) {
            return 0;
        }
        //current year, start year and end year match
        if (currentYear === startYear && currentYear === endYear) {
            return endMonth - startMonth + 1;
        }

        if (currentYear === startYear) {
            return 12 - (startMonth - 1); // Current year and start year match
        }
        if (currentYear === endYear) {
            if (currentMonth <= endMonth) {
                return endMonth; // Current year and end year match, and current month <= end month
            } else {
                return 0; // Current month is after the end month
            }
        }
        return 12; // Default case when current year is between start year and end year
    };
    const calculateInitialAmount = (accounts = []) => {
        const { incomeSource = [], investmentSource = [] } = selectedGoal?.sources || {};
        const sumIncomeSource = sumBy(
            incomeSource,
            ({ isSelected, accountBalance, beginningYear = '01-01-2025', endYear = '01-01-2025', frequency }: any) => {
                if (!isSelected) {
                    return 0;
                }
                let partsbeginningYear = beginningYear.split('-');
                let partsendYear = endYear.split('-');
                // Rearrange the parts to mm-dd-yyyy format
                let formattedDatebeginningYear = `${partsbeginningYear[1]}-${partsbeginningYear[0]}-${partsbeginningYear[2]}`;
                let formattedendYear = `${partsendYear[1]}-${partsendYear[0]}-${partsendYear[2]}`;
                const result = calculateAmountAfterMonths(formattedDatebeginningYear, formattedendYear);
                const updatedAmount =
                    frequency === 'Annually' ? result * accountBalance : result * (accountBalance / 12);
                return updatedAmount;
            }
        );

        const sumInvestmentSource = sumBy(
            investmentSource,
            ({
                isRCSelected,
                recurringContributions,
                recurringContributionsStartDate,
                recurringContributionsEndDate
            }: any) => {
                if (!isRCSelected) {
                    return 0;
                }
                const result = calculateAmountAfterMonths(
                    recurringContributionsStartDate || '01-01-2025',
                    recurringContributionsEndDate || '01-01-2025'
                );
                const updatedAmount = result * recurringContributions;
                return updatedAmount;
            }
        );

        const annualContribution = sumIncomeSource + sumInvestmentSource;

        const initialInvestment = sumBy(accounts, 'amount') || 0;
        return { initialInvestment, annualContribution };
    };
    const calculatedAmountval = calculateInitialAmount(selectedGoalRequest?.accounts);
    const initialInvestmentAmount = isFlow2
        ? goePayload.initialInvestment
        : selectedGoalRequest?.isIWSApplied
        ? selectedGoalRequest?.recommendedWealthSplit
        : calculatedAmountval.initialInvestment;
    // calculatedAmountval.initialInvestment is replaced with goePayload.initialInvestment for Flow2 as per AEX-2897 discussions
    // Disabled runpipe and GC ref: AEX-2897

    const anualContributionDict = useMemo(() => {
        return calculateAnnualContribution(goePayload);
    }, [
        goePayload,
        calculatedAmountval.annualContribution,
        isRecommendationApplied,
        goalId,
        adviceGoals.infusionindex
    ]);
    const renderGoalPriority = () => {
        return (
            <SectionBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LabelLarge>{t('TEXT_GOAL_PRIORITY')}</LabelLarge>
                    <InfoTooltip message={t('PRIORITY_LEVEL_OF_THIS_GOAL')} placement="top-end" />
                </Box>
                <StyledChipContainer sx={{ marginTop: '8px', width: 'fit-content', height: 'auto' }}>
                    <GoalPriorityChip goalPriority={selectedGoal?.goalPriority} />
                </StyledChipContainer>
            </SectionBox>
        );
    };

    const renderInitialInvestment = () => (
        <SectionBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LabelLarge label={t('LABEL_INITIAL_INVESTMENT')} />
                <InfoTooltip message={t('INITIAL_INVESTMENT_INFO_TEXT')} placement="top-end" />
            </Box>
            <ValueText>{formatCurrency(initialInvestmentAmount) || '-'}</ValueText>
        </SectionBox>
    );

    const renderAnnualContributions = () => (
        <SectionBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LabelLarge t="NEXT_CONTRIBUTION_HEADER" />
                <InfoTooltip
                    message={
                        anualContributionDict?.isFinalYear
                            ? t('ANNUAL_CONTRIBUTIONS_FINAL_YEAR_INFO_TEXT')
                            : anualContributionDict?.infusion == 0
                            ? ( isFlow2 ? t('NO_CONTRIBUTION_INFO_TEXT') : t('NO_ANNUAL_CONTRIBUTION_INFO_TEXT'))
                            : t('ANNUAL_CONTRIBUTIONS_INFO_TEXT', {
                                  realisationDt: anualContributionDict?.realisationDt
                              })
                    }
                    placement="top-end"
                />
            </Box>
            <ContributionDtValueText>{formatCurrency(anualContributionDict?.infusion || 0)}</ContributionDtValueText>
            {anualContributionDict?.realisationDt && anualContributionDict?.infusion != 0 && (
                <ContributionDt>
                    {dayjs(anualContributionDict.realisationDt, 'DD-MM-YYYY').format('MMM YYYY')}
                </ContributionDt>
            )}
        </SectionBox>
    );

    const renderLegacyAmount = () => (
        <SectionBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LabelLarge label={t('GOAL_SUMMARY_LEGACY_TEXT')} />
                <InfoTooltip message={t('BEQUEST_AMOUNT_HELPER_TEXT')} placement="top-end" />
            </Box>
            <ValueText>{formatCurrency(selectedGoal?.goalAmount || 0)}</ValueText>
        </SectionBox>
    );

    const getFormattedDate = () => {
        if (!selectedGoal?.targetDate && !selectedGoal?.goalDate) {
            return '-';
        }
        const date = dayjs(selectedGoal?.goalDate || selectedGoal?.targetDate, 'DD-MM-YYYY');
        if (!date.isValid()) {
            return '-';
        }
        return date.format('MMM, YYYY');
    };

    const renderTargetDate = () => (
        <SectionBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LabelLarge label={selectedGoal?.data?.isRetirement ? t('RETIREMENT_DATE') : t('TEXT_GOAL_DATE')} />
                <InfoTooltip
                    message={t(
                        selectedGoal?.data?.isRetirement
                            ? 'YEARS_TO_RETIREMENT_INFO_TEXT'
                            : 'WEALTH_YEARS_TO_GOAL_INFO_TEXT'
                    )}
                    placement="top-end"
                />
            </Box>
            <ValueText>{getFormattedDate()}</ValueText>
        </SectionBox>
    );

    const renderRetirementAndPlanningDate = () => (
        <SectionBox>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LabelLarge label={t('LABEL_RETIREMENT_VS_PLANNING_DATE')} />
                <InfoTooltip
                    message={t('RETIREMENT_DATE_INFO_TEXT') + '|' + t('YEARS_IN_RETIREMENT_INFO_TEXT')}
                    placement="top-end"
                />
            </Box>
            <ValueText>
                {/* non-breaking spaces so the gap around | isn't collapsed by HTML */}
                {`${
                    selectedGoal?.planStartRetirement
                        ? dayjs(selectedGoal?.planStartRetirement, 'DD-MM-YYYY').format('MMM, YYYY')
                        : '-'
                }`}
                <span style={{ color: theme.palette.semantic.text.muted, fontWeight: 500, padding: '0.25rem' }}>
                    {' '}
                    |{' '}
                </span>
                {`${
                    selectedGoal?.targetDate ? dayjs(selectedGoal?.targetDate, 'DD-MM-YYYY').format('MMM, YYYY') : '-'
                }`}
            </ValueText>
        </SectionBox>
    );

    return (
        <Column>
            {renderGoalPriority()}
            {renderInitialInvestment()}
            {renderAnnualContributions()}
            {/* Render Legacy Amount, even if its zero, for Retirement Goals */}
            {isRetirement && !!selectedGoal?.goalAmount && renderLegacyAmount()}
            {/* Render Target Date for Wealth Goals*/}
            {!isRetirement && renderTargetDate()}
            {/* Render Retirement Date and Planning Date for Retirement Goals */}
            {isRetirement && renderRetirementAndPlanningDate()}
        </Column>
    );
};
